/**
 * The Text step's rows → the overlay list state should hold (round-2 §2.4).
 * PURE, so the placement and timing rules are unit-tested in node; the step
 * (`screens/brandSetup/steps/SignoffStep.tsx`) only owns the inputs.
 *
 * Three rules:
 *   1. An EMPTY row is no line. Nothing is written for it — "no text, no
 *      line", the same answer the old single field gave.
 *   2. An existing line keeps everything but its text. A reel line that plays
 *      the last ten seconds top-left still does after its wording is fixed.
 *   3. A NEW line is a `DEFAULT_OVERLAY`. The one at index 0 takes the
 *      sample's preview timing (`SIGNOFF_PREVIEW_*`; `buildLayoutPayload` puts
 *      the shipped timing back) — exactly the old step's behaviour — and a new
 *      line never lands ON another line: `DEFAULT_OVERLAY`'s spot is the
 *      top-left corner, and two lines there rendered as one garbled block on
 *      the phone. A new line takes the first free slot one line-step down.
 */
import type { RenderTextOverlay } from "../../../../src/remotion/types.ts";
import { DEFAULT_OVERLAY } from "../overlaysEdit";
import { SIGNOFF_PREVIEW_DURATION_SEC, SIGNOFF_PREVIEW_START } from "./payload";

export interface TextLineRow {
  text: string;
  /** The overlay this row edits, or null for a line added on the step. */
  base: RenderTextOverlay | null;
}

/**
 * One line-step down the frame, in % of its height. A 56 px overlay on the
 * 3840-tall canvas is ~1.5 % tall; 2.5 % stacks lines with a little air.
 */
export const LINE_STEP_PCT = 2.5;
/** Never stack a line off the bottom of the frame. */
const MAX_Y_PCT = 90;

/**
 * Where a new line goes given the lines already on the video: the default
 * spot, or the first slot below it that no line at that x already occupies.
 * Deterministic — the same list always yields the same slot.
 */
export function placeNewLine(existing: readonly Pick<RenderTextOverlay, "xPct" | "yPct">[]): { xPct: number; yPct: number } {
  const xPct = DEFAULT_OVERLAY.xPct;
  let yPct = DEFAULT_OVERLAY.yPct;
  const occupied = (y: number): boolean =>
    existing.some((o) => o.xPct === xPct && Math.abs(o.yPct - y) < LINE_STEP_PCT);
  while (occupied(yPct) && yPct + LINE_STEP_PCT <= MAX_Y_PCT) yPct += LINE_STEP_PCT;
  return { xPct, yPct };
}

/** Rows → overlays, in row order. */
export function overlaysFromRows(rows: readonly TextLineRow[]): RenderTextOverlay[] {
  const out: RenderTextOverlay[] = [];
  // Every line that already has a spot claims it up front, whatever its
  // row order — a new line is placed against ALL of them, not just the ones
  // above it in the list.
  const placed: Array<Pick<RenderTextOverlay, "xPct" | "yPct">> = rows
    .filter((r) => r.base && r.text.trim())
    .map((r) => ({ xPct: r.base!.xPct, yPct: r.base!.yPct }));
  for (const row of rows) {
    const text = row.text.trim();
    if (!text) continue;
    if (row.base) {
      out.push({ ...row.base, text });
      continue;
    }
    const spot = placeNewLine(placed);
    placed.push(spot);
    out.push(
      out.length === 0
        ? { ...DEFAULT_OVERLAY, ...spot, text, start: SIGNOFF_PREVIEW_START, durationSec: SIGNOFF_PREVIEW_DURATION_SEC }
        : { ...DEFAULT_OVERLAY, ...spot, text },
    );
  }
  return out;
}
